/**
 * Version Checking and Polling Module
 * 
 * This module provides a safety net for update detection by:
 * - Polling /version.json periodically
 * - Comparing deployment IDs
 * - Dispatching update events when new versions are detected
 * - Optional WebSocket support for real-time updates
 */

interface VersionInfo {
  deploymentId: string;
  buildTs: number;
  git?: {
    branch?: string;
    commit?: string;
  };
}

let currentDeploymentId: string;
let pollingInterval: number | null = null;
let lastCheckTime = 0;
const hasShownUpdateForVersion: Set<string> = new Set();

/**
 * Initialize version checking
 * Starts polling for version updates as a safety net
 */
export function initVersionCheck() {
  // Get current deployment ID from build-time env
  currentDeploymentId = import.meta.env.VITE_DEPLOYMENT_ID || 'dev';
  
  
  // Start polling (every 5 minutes)
  startPolling();
  
  // Optional: Initialize WebSocket listener if enabled
  if (import.meta.env.VITE_ENABLE_WS_UPDATES === 'true') {
    initWebSocketListener();
  }
}

/**
 * Start polling for version updates
 */
function startPolling() {
  // Clear any existing interval
  if (pollingInterval) {
    clearInterval(pollingInterval);
  }
  
  // Check immediately on start
  checkVersion();
  
  // Poll every 5 minutes (300000ms)
  pollingInterval = window.setInterval(() => {
    checkVersion();
  }, 5 * 60 * 1000);
}

/**
 * Stop version polling
 */
export function stopVersionCheck() {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
}

/**
 * Check for new version by fetching version.json
 */
async function checkVersion() {
  // Throttle checks to avoid rapid polling
  const now = Date.now();
  if (now - lastCheckTime < 10000) { // Min 10 seconds between checks
    return;
  }
  lastCheckTime = now;
  
  try {
    // Fetch version.json with cache busting
    const timestamp = Date.now();
    const response = await fetch(`/version.json?t=${timestamp}`, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok) {
      console.warn('[Version Check] Failed to fetch version.json:', response.status);
      return;
    }
    
    const versionInfo: VersionInfo = await response.json();
    
    // Compare deployment IDs
    if (versionInfo.deploymentId && 
        versionInfo.deploymentId !== currentDeploymentId &&
        versionInfo.deploymentId !== 'dev' &&
        !hasShownUpdateForVersion.has(versionInfo.deploymentId)) {
      
      
      // Mark this version as shown to avoid duplicate dialogs
      hasShownUpdateForVersion.add(versionInfo.deploymentId);
      
      // Dispatch update event
      window.dispatchEvent(new CustomEvent('app-update-available', {
        detail: {
          source: 'version-check',
          currentVersion: currentDeploymentId,
          newVersion: versionInfo.deploymentId,
          buildTime: versionInfo.buildTs
        }
      }));
    }
  } catch (error) {
    // Silently fail - version check is a safety net, not critical
    console.debug('[Version Check] Check failed:', error);
  }
}

/**
 * WebSocket listener for real-time update notifications (optional)
 * This is a stub implementation - wire up to your WebSocket server if available
 */
function initWebSocketListener() {
  // This is disabled by default - enable via VITE_ENABLE_WS_UPDATES env var
  // and configure VITE_WS_URL to point to your WebSocket server
  
  const wsUrl = import.meta.env.VITE_WS_URL;
  if (!wsUrl) {
    console.debug('[Version Check] WebSocket URL not configured');
    return;
  }
  
  try {
    const ws = new WebSocket(wsUrl);
    
    ws.addEventListener('open', () => {
      
      // Subscribe to version updates
      ws.send(JSON.stringify({
        type: 'subscribe',
        channel: 'app-version'
      }));
    });
    
    ws.addEventListener('message', (event) => {
      try {
        const message = JSON.parse(event.data);
        
        // Handle version update messages
        if (message.type === 'app-version' && message.deploymentId) {
          if (message.deploymentId !== currentDeploymentId &&
              message.deploymentId !== 'dev' &&
              !hasShownUpdateForVersion.has(message.deploymentId)) {
            
            
            hasShownUpdateForVersion.add(message.deploymentId);
            
            // Dispatch update event
            window.dispatchEvent(new CustomEvent('app-update-available', {
              detail: {
                source: 'websocket',
                currentVersion: currentDeploymentId,
                newVersion: message.deploymentId,
                buildTime: message.buildTime
              }
            }));
          }
        }
      } catch (err) {
        console.debug('[Version Check] WebSocket message parse error:', err);
      }
    });
    
    ws.addEventListener('error', (error) => {
      console.debug('[Version Check] WebSocket error:', error);
    });
    
    ws.addEventListener('close', () => {
      console.debug('[Version Check] WebSocket disconnected');
      // Could implement reconnection logic here if needed
    });
    
  } catch (error) {
    console.debug('[Version Check] Failed to initialize WebSocket:', error);
  }
}

/**
 * Force a version check immediately
 */
export function forceVersionCheck() {
  checkVersion();
}

/**
 * Reset the shown versions cache (useful after user clicks "Later")
 */
export function resetVersionCache() {
  // Clear cache but keep current version to allow re-showing the dialog
  const temp = Array.from(hasShownUpdateForVersion);
  hasShownUpdateForVersion.clear();
  // Re-add all except the most recent
  if (temp.length > 1) {
    temp.slice(0, -1).forEach(v => hasShownUpdateForVersion.add(v));
  }
}